import { useState, useEffect, useCallback, useRef } from 'react';
import { habitsApi } from '../api';
import { habitOfflineStorage } from '../utils/offlineStorage';
import { useAuth } from '../contexts/AuthContext';

export function useHabits() {
  const { user } = useAuth();
  const isOnline = !!user;

  const [habits,  setHabits]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const fetchingRef = useRef(false);

  // Unwrap both { data: [...] } and plain array responses
  const unwrap = (res, fb) => {
    if (!res?.data) return fb;
    if (res.data?.data !== undefined) return res.data.data;
    return Array.isArray(res.data) ? res.data : fb;
  };

  const unwrapOne = (res) => {
    if (!res?.data) return null;
    if (res.data?.data !== undefined) return res.data.data;
    return res.data ?? null;
  };

  const fetchHabits = useCallback(async () => {
    // Always reset ref so retries work (e.g. isOnline flips true)
    fetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      if (isOnline) {
        const res = await habitsApi.list(7);
        const data = unwrap(res, []);
        setHabits(Array.isArray(data) ? data : []);
      } else {
        const offline = habitOfflineStorage.getAll();
        setHabits(Array.isArray(offline) ? offline : []);
      }
    } catch (e) {
      console.error('useHabits fetch error:', e);
      setError(e);
      try {
        setHabits(habitOfflineStorage.getAll() ?? []);
      } catch {
        setHabits([]);
      }
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [isOnline]); // re-runs when user logs in/out

  useEffect(() => {
    fetchHabits();
  }, [fetchHabits]);

  // ── Add ───────────────────────────────────────────────────────────────
  const addHabit = useCallback(async (payload) => {
    if (isOnline) {
      const res = await habitsApi.create(payload);
      const created = unwrapOne(res) ?? payload;
      setHabits(prev => [...prev, created]);
      return created;
    } else {
      const h = habitOfflineStorage.add(payload);
      setHabits(prev => [...prev, h]);
      return h;
    }
  }, [isOnline]);

  // ── Update ────────────────────────────────────────────────────────────
  const updateHabit = useCallback(async (id, payload) => {
    if (isOnline) {
      const res = await habitsApi.update(id, payload);
      const updated = unwrapOne(res) ?? payload;
      setHabits(prev => prev.map(h => h.id === id ? updated : h));
      return updated;
    } else {
      const updated = habitOfflineStorage.update(id, payload);
      setHabits(prev => prev.map(h => h.id === id ? updated : h));
      return updated;
    }
  }, [isOnline]);

  // ── Remove ────────────────────────────────────────────────────────────
  const removeHabit = useCallback(async (id) => {
    setHabits(prev => prev.filter(h => h.id !== id));
    if (isOnline) {
      try { await habitsApi.remove(id); }
      catch { fetchHabits(); }
    } else {
      habitOfflineStorage.remove(id);
    }
  }, [isOnline, fetchHabits]);

  // ── Toggle ────────────────────────────────────────────────────────────
  const toggleHabit = useCallback(async (id, date) => {
    // Optimistic
    setHabits(prev => prev.map(h => {
      if (h.id !== id) return h;
      const week = (h.week || []).map(d =>
        d.date === date ? { ...d, done: !d.done } : d
      );
      return { ...h, week };
    }));

    if (isOnline) {
      try {
        const res = await habitsApi.toggle(id, date);
        const updated = unwrapOne(res);
        if (updated) setHabits(prev => prev.map(h => h.id === id ? updated : h));
      } catch {
        // Rollback
        setHabits(prev => prev.map(h => {
          if (h.id !== id) return h;
          const week = (h.week || []).map(d =>
            d.date === date ? { ...d, done: !d.done } : d
          );
          return { ...h, week };
        }));
      }
    } else {
      const updated = habitOfflineStorage.toggle(id, date);
      if (updated) setHabits(prev => prev.map(h => h.id === id ? updated : h));
    }
  }, [isOnline]);

  // ── Reorder ───────────────────────────────────────────────────────────
  const reorderHabits = useCallback(async (reordered) => {
    setHabits(reordered);
    if (!isOnline) return;
    try {
      await habitsApi.reorder(
        reordered.map((h, i) => ({ id: h.id, sort_order: i }))
      );
    } catch {
      fetchHabits();
    }
  }, [isOnline, fetchHabits]);

  return {
    habits,
    loading,
    error,
    isOnline,
    refetch: fetchHabits,
    setHabits,
    addHabit,
    updateHabit,
    removeHabit,
    toggleHabit,
    reorderHabits,
  };
}